/**
 * Security Node ID Utilities
 *
 * Functions for creating and parsing security-related node IDs.
 * Follows the same pattern as nodeIdUtils.ts with the NODE_ID_SEPARATOR.
 */

import { createNodeId, parseNodeId } from './nodeIdUtils';

// ============================================================================
// MSSQL Server-Level Security Folder IDs
// ============================================================================

/**
 * Creates a server security folder node ID (MSSQL)
 */
export function createServerSecurityFolderNodeId(connectionId: string): string {
  return createNodeId('server-security-folder', connectionId);
}

/**
 * Creates a logins folder node ID (MSSQL)
 */
export function createLoginsFolderNodeId(connectionId: string): string {
  return createNodeId('logins-folder', connectionId);
}

/**
 * Creates a server roles folder node ID (MSSQL)
 */
export function createServerRolesFolderNodeId(connectionId: string): string {
  return createNodeId('server-roles-folder', connectionId);
}

// ============================================================================
// PostgreSQL Server-Level Security Folder IDs
// ============================================================================

/**
 * Creates a PostgreSQL roles folder node ID (server-level)
 */
export function createPgRolesFolderNodeId(connectionId: string): string {
  return createNodeId('pg-roles-folder', connectionId);
}

/**
 * Creates a login roles folder node ID (PostgreSQL)
 */
export function createLoginRolesFolderNodeId(connectionId: string): string {
  return createNodeId('login-roles-folder', connectionId);
}

/**
 * Creates a group roles folder node ID (PostgreSQL)
 */
export function createGroupRolesFolderNodeId(connectionId: string): string {
  return createNodeId('group-roles-folder', connectionId);
}

// ============================================================================
// Database-Level Security Folder IDs
// ============================================================================

/**
 * Creates a database security folder node ID
 */
export function createDatabaseSecurityFolderNodeId(
  connectionId: string,
  database: string
): string {
  return createNodeId('database-security-folder', connectionId, database);
}

/**
 * Creates a users folder node ID (MSSQL)
 */
export function createUsersFolderNodeId(
  connectionId: string,
  database: string
): string {
  return createNodeId('users-folder', connectionId, database);
}

/**
 * Creates a database roles folder node ID
 */
export function createDatabaseRolesFolderNodeId(
  connectionId: string,
  database: string
): string {
  return createNodeId('database-roles-folder', connectionId, database);
}

/**
 * Creates a schemas folder node ID
 */
export function createSchemasFolderNodeId(
  connectionId: string,
  database: string
): string {
  return createNodeId('schemas-folder', connectionId, database);
}

// ============================================================================
// Security Object Node IDs
// ============================================================================

/**
 * Creates a login node ID (MSSQL)
 */
export function createLoginNodeId(
  connectionId: string,
  loginName: string
): string {
  return createNodeId('login', connectionId, loginName);
}

/**
 * Creates a server role node ID (MSSQL)
 */
export function createServerRoleNodeId(
  connectionId: string,
  roleName: string
): string {
  return createNodeId('server-role', connectionId, roleName);
}

/**
 * Creates a database user node ID (MSSQL)
 */
export function createDatabaseUserNodeId(
  connectionId: string,
  database: string,
  userName: string
): string {
  return createNodeId('database-user', connectionId, database, userName);
}

/**
 * Creates a database role node ID
 */
export function createDatabaseRoleNodeId(
  connectionId: string,
  database: string,
  roleName: string
): string {
  return createNodeId('database-role', connectionId, database, roleName);
}

/**
 * Creates a schema node ID
 */
export function createSchemaNodeId(
  connectionId: string,
  database: string,
  schemaName: string
): string {
  return createNodeId('schema', connectionId, database, schemaName);
}

/**
 * Creates a login role node ID (PostgreSQL)
 */
export function createLoginRoleNodeId(
  connectionId: string,
  roleName: string
): string {
  return createNodeId('login-role', connectionId, roleName);
}

/**
 * Creates a group role node ID (PostgreSQL)
 */
export function createGroupRoleNodeId(
  connectionId: string,
  roleName: string
): string {
  return createNodeId('group-role', connectionId, roleName);
}

// ============================================================================
// Oracle Security Node IDs
// ============================================================================

/**
 * Creates an Oracle user node ID
 */
export function createOracleUserNodeId(
  connectionId: string,
  userName: string
): string {
  return createNodeId('oracle-user', connectionId, userName);
}

/**
 * Creates an Oracle role node ID
 */
export function createOracleRoleNodeId(
  connectionId: string,
  roleName: string
): string {
  return createNodeId('oracle-role', connectionId, roleName);
}

/**
 * Creates an Oracle roles folder node ID (server-level)
 */
export function createOracleRolesFolderNodeId(connectionId: string): string {
  return createNodeId('oracle-roles-folder', connectionId);
}

// ============================================================================
// Parsing Utilities
// ============================================================================

/**
 * Checks if a node ID is a security folder type
 */
export function isSecurityFolderNodeId(nodeId: string): boolean {
  const parts = parseNodeId(nodeId);
  const folderTypes = [
    'server-security-folder',
    'logins-folder',
    'server-roles-folder',
    'pg-roles-folder',
    'login-roles-folder',
    'group-roles-folder',
    'database-security-folder',
    'users-folder',
    'database-roles-folder',
    'schemas-folder',
    'oracle-roles-folder',
  ];
  return folderTypes.includes(parts[0]);
}

/**
 * Checks if a node ID is a security object type
 */
export function isSecurityObjectNodeId(nodeId: string): boolean {
  const parts = parseNodeId(nodeId);
  const objectTypes = [
    'login',
    'server-role',
    'database-user',
    'database-role',
    'schema',
    'login-role',
    'group-role',
    'oracle-user',
    'oracle-role',
  ];
  return objectTypes.includes(parts[0]);
}

/**
 * Gets the security principal name from a security object node ID
 */
export function getSecurityPrincipalNameFromNodeId(nodeId: string): string | undefined {
  const parts = parseNodeId(nodeId);

  // Server-level objects: type<->connectionId<->principalName
  if (['login', 'server-role', 'login-role', 'group-role', 'oracle-user', 'oracle-role'].includes(parts[0])) {
    return parts.length >= 3 ? parts[2] : undefined;
  }

  // Database-level objects: type<->connectionId<->database<->principalName
  if (['database-user', 'database-role', 'schema'].includes(parts[0])) {
    return parts.length >= 4 ? parts[3] : undefined;
  }

  return undefined;
}

/**
 * Gets the database from a database-level security node ID
 */
export function getDatabaseFromSecurityNodeId(nodeId: string): string | undefined {
  const parts = parseNodeId(nodeId);

  // Database-level folders and objects: type<->connectionId<->database[<->principalName]
  const databaseLevelTypes = [
    'database-security-folder',
    'users-folder',
    'database-roles-folder',
    'schemas-folder',
    'database-user',
    'database-role',
    'schema',
  ];

  if (databaseLevelTypes.includes(parts[0]) && parts.length >= 3) {
    return parts[2];
  }

  return undefined;
}
